import i18next from 'i18next';
import { LICENSE_REQUIRED_CODE, markLicenseRequired } from './licenseStatus';
import { markTermsAcceptanceRequired, TERMS_ACCEPTANCE_REQUIRED_CODE } from './termsStatus';
import { ACCOUNT_DELETED_CODE, markAccountDeleted } from './accountDeleted';
import { approvalStateOfCode, markAccountApproval } from './accountApproval';
import { clearStoredTokens, readStoredTokens } from './authTokens';
import { markSessionExpired } from './sessionExpired';

const BASE_URL = (import.meta as unknown as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL ?? '';

export interface ApiClientOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  params?: Record<string, unknown>;
}

export class ApiError extends Error {
  status: number;
  code: string;
  data?: unknown;

  constructor(status: number, code: string, message: string, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.data = data;
  }
}

// ── Silent session refresh ──────────────────────────────────────────────────

/** Result of one refresh attempt (see lib/sessionRefresh.ts). */
export type SessionRefreshOutcome = 'refreshed' | 'invalid' | 'failed';
export type SessionRefresher = () => Promise<SessionRefreshOutcome>;

let sessionRefresher: SessionRefresher | null = null;

/**
 * Registers the function apiClient calls once when an authenticated request
 * answers 401 (lib/sessionRefresh.ts registers itself; AuthProvider imports
 * it). The refresher must be single-flight. Without one, a 401 ends the
 * session straight away. Returns a function that unregisters it.
 */
export function setSessionRefresher(refresher: SessionRefresher | null): () => void {
  sessionRefresher = refresher;
  return () => {
    if (sessionRefresher === refresher) sessionRefresher = null;
  };
}

/** /api/auth/* (login, refresh, logout, …) never triggers a refresh or a sign-out. */
function isAuthEndpoint(path: string): boolean {
  try {
    return /^\/api\/auth(\/|$)/.test(new URL(path, 'http://x').pathname);
  } catch {
    return false;
  }
}

/**
 * Renews the session after `sentToken` was rejected. True when a newer access
 * token is available — either another request refreshed meanwhile, or this
 * refresh succeeded.
 */
async function renewSession(sentToken: string): Promise<boolean> {
  const current = readStoredTokens();
  if (current && current.accessToken !== sentToken) return true;
  if (!current || current.refreshToken === '' || !sessionRefresher) return false;
  try {
    return (await sessionRefresher()) === 'refreshed';
  } catch {
    return false;
  }
}

/** The session is over: drop it and let AuthProvider sign out ("Session expired"). */
function endSession(): void {
  clearStoredTokens();
  markSessionExpired();
}

// ── URL + headers ───────────────────────────────────────────────────────────

/**
 * Joins the API base URL, a request path and query params into one URL.
 * - `params` become the query string (undefined/null skipped, arrays repeated) —
 *   they used to be dropped silently, so GET/DELETE /api/v1/ai-feedback went out
 *   without outputType/outputId and the API answered 400.
 * - A base URL that already ends in `/api` (or has a trailing slash) never yields
 *   `/api/api/…` or `//`.
 */
export function buildApiUrl(
  path: string,
  params?: Record<string, unknown>,
  baseUrl: string = BASE_URL,
): string {
  const base = baseUrl.replace(/\/+$/, '');
  let rel = path.startsWith('/') || /^https?:\/\//i.test(path) ? path : `/${path}`;
  if (/\/api$/i.test(base) && /^\/api(\/|$)/i.test(rel)) rel = rel.slice(4) || '/';
  let url = /^https?:\/\//i.test(rel) ? rel : `${base}${rel}`;

  if (params) {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null) continue;
      for (const v of Array.isArray(value) ? value : [value]) {
        if (v === undefined || v === null) continue;
        query.append(key, v instanceof Date ? v.toISOString() : String(v));
      }
    }
    const qs = query.toString();
    if (qs) url += `${url.includes('?') ? '&' : '?'}${qs}`;
  }
  return url;
}

/** The stored access token ('' when signed out). */
function currentAccessToken(): string {
  return readStoredTokens()?.accessToken ?? '';
}

/**
 * The app's UI language as an `Accept-Language` header, so AI features answer in
 * it (the backend validates it against the supported languages, default en).
 * Uses the shared i18next instance; empty before i18n is initialised.
 */
export function getLanguageHeader(): Record<string, string> {
  const language = i18next.resolvedLanguage ?? i18next.language;
  if (typeof language !== 'string') return {};
  // A plain language tag ("de", "en-GB"); anything else is not sent.
  const [base = '', region, ...rest] = language.split('-');
  const valid =
    rest.length === 0 &&
    /^[a-z]{2,3}$/.test(base) &&
    (region === undefined || /^[A-Za-z0-9]{2,8}$/.test(region));
  return valid ? { 'Accept-Language': language } : {};
}

function authHeaderFor(token: string): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Error handling ──────────────────────────────────────────────────────────

interface ParsedError {
  code: string;
  message: string;
  data: unknown;
}

async function parseError(response: Response): Promise<ParsedError> {
  let errorData: unknown;
  try {
    errorData = await response.json();
  } catch {
    errorData = undefined;
  }
  const errorObj =
    typeof errorData === 'object' &&
    errorData !== null &&
    'error' in errorData &&
    typeof (errorData as Record<string, unknown>).error === 'object' &&
    (errorData as Record<string, unknown>).error !== null
      ? ((errorData as Record<string, unknown>).error as Record<string, unknown>)
      : null;
  return {
    code: typeof errorObj?.code === 'string' ? errorObj.code : 'API_ERROR',
    message: typeof errorObj?.message === 'string' ? errorObj.message : `Request failed with status ${response.status}`,
    data: errorData,
  };
}

/**
 * Sends a request with the stored access token. On a 401 that means "this
 * session is no longer valid" it renews the session ONCE (single-flight —
 * concurrent 401s share one refresh) and retries with the new token; only when
 * that fails does it end the session. Never loops: the retry is not retried.
 *
 * Not a session problem, never refreshed or signed out:
 *  - third-party provider errors (`PROVIDER_*`, #1619),
 *  - /api/auth/* (a failed login or refresh),
 *  - 401 ACCOUNT_DELETED (the account is gone; AuthProvider signs out).
 */
async function sendAuthorized(
  path: string,
  send: (authHeader: Record<string, string>) => Promise<Response>,
  usesStoredToken: boolean,
): Promise<Response> {
  const sentToken = usesStoredToken ? currentAccessToken() : '';
  const response = await send(authHeaderFor(sentToken));
  if (response.ok) return response;

  const error = await parseError(response);

  if (response.status === 401 && !error.code.startsWith('PROVIDER_') && !isAuthEndpoint(path)) {
    if (error.code === ACCOUNT_DELETED_CODE) {
      clearStoredTokens();
      markAccountDeleted();
      throw new ApiError(401, error.code, 'Session expired. Please log in again.', error.data);
    }
    if (sentToken !== '' && (await renewSession(sentToken))) {
      const retried = await send(authHeaderFor(currentAccessToken()));
      if (retried.ok) return retried;
      const retryError = await parseError(retried);
      if (retried.status !== 401 || retryError.code.startsWith('PROVIDER_')) {
        throwApiError(retried.status, retryError);
      }
      if (retryError.code === ACCOUNT_DELETED_CODE) {
        clearStoredTokens();
        markAccountDeleted();
        throw new ApiError(401, retryError.code, 'Session expired. Please log in again.', retryError.data);
      }
    }
    // Only a rejected session "expires" — a request sent without a token
    // (e.g. a query refetching right after logout) must not show the notice.
    if (sentToken !== '') endSession();
    else clearStoredTokens();
    throw new ApiError(401, error.code, 'Session expired. Please log in again.', error.data);
  }

  throwApiError(response.status, error);
}

function throwApiError(status: number, error: ParsedError): never {
  if (status === 403 && error.code === LICENSE_REQUIRED_CODE) {
    // No active licence: AppLayout's LicenseRequiredNotice explains it (translated).
    markLicenseRequired();
  }
  if (status === 403 && error.code === TERMS_ACCEPTANCE_REQUIRED_CODE) {
    // Read-only until the new terms are accepted: AppLayout's TermsReadOnlyNotice explains it.
    markTermsAcceptanceRequired();
  }
  const approval = status === 403 ? approvalStateOfCode(error.code) : null;
  if (approval !== null) {
    // Closed beta: the account is not (yet) approved. No refresh, no retry —
    // AuthProvider revokes and drops the session, the app shows /account-pending.
    markAccountApproval(approval);
  }
  throw new ApiError(status, error.code, error.message, error.data);
}

async function apiClientCore<T = unknown>(
  path: string,
  options: ApiClientOptions = {},
): Promise<T> {
  const { method = 'GET', body, headers: extraHeaders = {}, signal, params } = options;

  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  let fetchBody: BodyInit | undefined;
  if (body !== undefined) {
    fetchBody = isFormData
      ? (body as FormData)
      : typeof body === 'string'
        ? body
        : JSON.stringify(body as Record<string, unknown>);
  }

  const url = buildApiUrl(path, params);
  // A caller-supplied Authorization header is used as is (no refresh).
  const usesStoredToken = !Object.keys(extraHeaders).some((h) => h.toLowerCase() === 'authorization');

  const response = await sendAuthorized(
    path,
    (authHeader) =>
      fetch(url, {
        method,
        headers: {
          // Let the browser set Content-Type for FormData (it must include the multipart boundary)
          ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
          ...getLanguageHeader(),
          ...authHeader,
          ...extraHeaders,
        },
        body: fetchBody,
        signal,
      }),
    usesStoredToken,
  );

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export interface ApiClientWithMethods {
  <T = unknown>(path: string, options?: ApiClientOptions): Promise<T>;
  get<T = unknown>(path: string, options?: ApiClientOptions): Promise<T>;
  post<T = unknown>(path: string, body?: unknown, options?: ApiClientOptions): Promise<T>;
  put<T = unknown>(path: string, body?: unknown, options?: ApiClientOptions): Promise<T>;
  patch<T = unknown>(path: string, body?: unknown, options?: ApiClientOptions): Promise<T>;
  delete<T = unknown>(path: string, options?: ApiClientOptions): Promise<T>;
  /**
   * POST that returns the raw Response for SSE/streaming consumption (read it
   * with lib/sse.ts readSseEvents). Same token handling as every request,
   * incl. the single refresh-and-retry on a 401. Throws ApiError on non-2xx;
   * `signal` aborts the request and the body stream.
   */
  streamPost(path: string, body?: unknown, options?: Pick<ApiClientOptions, 'signal'>): Promise<Response>;
  /**
   * GET of a binary file (image, PDF …) with the same token handling as every
   * request. Resolves with the body as a Blob and the file name from
   * Content-Disposition (null when absent). Throws ApiError on non-2xx.
   */
  getBlob(path: string, options?: Pick<ApiClientOptions, 'signal' | 'params'>): Promise<{ blob: Blob; filename: string | null }>;
}

function createApiClient(): ApiClientWithMethods {
  const client = <T = unknown>(path: string, options: ApiClientOptions = {}): Promise<T> =>
    apiClientCore<T>(path, options);

  client.get = <T = unknown>(path: string, options: ApiClientOptions = {}): Promise<T> =>
    apiClientCore<T>(path, { ...options, method: 'GET' });

  client.post = <T = unknown>(path: string, body?: unknown, options: ApiClientOptions = {}): Promise<T> =>
    apiClientCore<T>(path, { ...options, method: 'POST', body });

  client.put = <T = unknown>(path: string, body?: unknown, options: ApiClientOptions = {}): Promise<T> =>
    apiClientCore<T>(path, { ...options, method: 'PUT', body });

  client.patch = <T = unknown>(path: string, body?: unknown, options: ApiClientOptions = {}): Promise<T> =>
    apiClientCore<T>(path, { ...options, method: 'PATCH', body });

  client.delete = <T = unknown>(path: string, options: ApiClientOptions = {}): Promise<T> =>
    apiClientCore<T>(path, { ...options, method: 'DELETE' });

  client.streamPost = async (path: string, body?: unknown, options: Pick<ApiClientOptions, 'signal'> = {}): Promise<Response> => {
    const fetchBody = body !== undefined ? JSON.stringify(body as Record<string, unknown>) : undefined;
    return sendAuthorized(
      path,
      (authHeader) =>
        fetch(buildApiUrl(path), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...getLanguageHeader(), ...authHeader },
          body: fetchBody,
          ...(options.signal ? { signal: options.signal } : {}),
        }),
      true,
    );
  };

  client.getBlob = async (path: string, options: Pick<ApiClientOptions, 'signal' | 'params'> = {}) => {
    const response = await sendAuthorized(
      path,
      (authHeader) =>
        fetch(buildApiUrl(path, options.params), {
          method: 'GET',
          headers: { ...getLanguageHeader(), ...authHeader },
          ...(options.signal ? { signal: options.signal } : {}),
        }),
      true,
    );
    const disposition = response.headers.get('Content-Disposition') ?? '';
    const match = /filename="([^"]+)"/.exec(disposition);
    return { blob: await response.blob(), filename: match ? match[1]! : null };
  };

  return client as ApiClientWithMethods;
}

export const apiClient = createApiClient();
